import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

interface SizeValueProps extends Partial<MenuItemProps> {
    value: string | undefined;
    isStatic?: boolean;
}

const SizeValue = ({
    value,
    isStatic = false,
    ...menuItemProps
}: SizeValueProps): ReactElement => {
    const { palette } = useTheme();

    const interactionSx =
        isStatic || menuItemProps.disabled
            ? { ['pointer-events']: 'none !important' }
            : undefined;

    const getColor = useCallback(
        (part: string): string => {
            if (menuItemProps.disabled) {
                return palette.text.disabled;
            }
            return part.length !== 1
                ? alpha(palette.primary.main, 0.7)
                : alpha(palette.primary.main, 0.2);
        },
        [menuItemProps.disabled, palette.primary.main, palette.text.disabled]
    );

    const valueContent = useMemo(() => {
        if (!value) return 'None';

        return value.split(' ').map((part, index) => (
            <span
                key={`${value}-part-${index}-${part}`}
                style={{
                    display: 'inline',
                    color: getColor(part),
                }}
            >
                {part}
            </span>
        ));
    }, [getColor, value]);

    return (
        <MenuList
            sx={{ display: 'contents' }}
            disablePadding
        >
            <MenuItem
                {...menuItemProps}
                sx={{
                    ...interactionSx,
                    flexGrow: 1,
                    height: 1,
                }}
                disableRipple
                disableTouchRipple
            >
                <Typography
                    variant="captions.default"
                    sx={{
                        whiteSpace: 'normal', // Enables normal wrapping at whitespace
                        wordBreak: 'break-word', // Extra fallback for breaking long words
                        maxWidth: 'calc(100% - 56px)', // Leave space for the icon
                    }}
                >
                    {valueContent}
                </Typography>
            </MenuItem>
        </MenuList>
    );
};

export { SizeValue };
